import {
  DateTimePicker,
  DateTimePickerProps,
  renderTimeViewClock,
} from '@mui/x-date-pickers-pro';
import { forwardRef } from 'react';

import { dateTimeToDate, dateToDateTime } from 'helpers/datetime';

import TimezoneTextField from './components/TimezoneTextField';

import type { TextFieldProps } from '@mui/material';
import type { DateTime } from 'luxon';

type OmittedDateTimePickerProps =
  | 'components'
  | 'componentsProps'
  | 'localeText'
  | 'slotProps'
  | 'slots'
  | 'viewRenderers';

export interface LocatedDateTimePickerProps extends Omit<
  DateTimePickerProps<Date>,
  OmittedDateTimePickerProps
> {
  showCurrentTimeZone?: boolean;
  TextFieldProps?: TextFieldProps;
  timezone?: string;
}

const LocatedDateTimePicker = forwardRef<
  HTMLDivElement,
  LocatedDateTimePickerProps
>(
  (
    {
      showCurrentTimeZone = true,
      TextFieldProps,
      timezone,
      ...props
    }: LocatedDateTimePickerProps,
    ref,
  ) => {
    /**
     * Convert between Date and DateTime props due to the usage of Luxon DateTime in the
     * MUI DateTimePicker component
     */
    const convertedDateTimePickerProps: Omit<
      DateTimePickerProps<DateTime>,
      OmittedDateTimePickerProps
    > = {
      ...props,
      dayOfWeekFormatter:
        props.dayOfWeekFormatter &&
        ((day, date) => props.dayOfWeekFormatter!(day, dateTimeToDate(date))),
      defaultCalendarMonth:
        props.defaultCalendarMonth &&
        dateToDateTime(props.defaultCalendarMonth, { timezone }),
      defaultValue:
        props.defaultValue && dateToDateTime(props.defaultValue, { timezone }),
      maxDate: props.maxDate && dateToDateTime(props.maxDate, { timezone }),
      maxDateTime:
        props.maxDateTime && dateToDateTime(props.maxDateTime, { timezone }),
      maxTime: props.maxTime && dateToDateTime(props.maxTime, { timezone }),
      minDate: props.minDate && dateToDateTime(props.minDate, { timezone }),
      minDateTime:
        props.minDateTime && dateToDateTime(props.minDateTime, { timezone }),
      minTime: props.minTime && dateToDateTime(props.minTime, { timezone }),
      onAccept:
        props.onAccept &&
        ((value) => props.onAccept!(value ? dateTimeToDate(value) : null)),
      onChange:
        props.onChange &&
        ((value, context) =>
          props.onChange!(value ? dateTimeToDate(value) : null, context)),
      onError:
        props.onError &&
        ((error, value) =>
          props.onError!(error, value ? dateTimeToDate(value) : null)),
      onMonthChange:
        props.onMonthChange &&
        ((month) => props.onMonthChange!(dateTimeToDate(month))),
      onYearChange:
        props.onYearChange &&
        ((year) => props.onYearChange!(dateTimeToDate(year))),
      referenceDate:
        props.referenceDate &&
        dateToDateTime(props.referenceDate, { timezone }),
      shouldDisableDate:
        props.shouldDisableDate &&
        ((day) => props.shouldDisableDate!(dateTimeToDate(day))),
      shouldDisableMonth:
        props.shouldDisableMonth &&
        ((month) => props.shouldDisableMonth!(dateTimeToDate(month))),
      shouldDisableTime:
        props.shouldDisableTime &&
        ((value, view) =>
          props.shouldDisableTime!(dateTimeToDate(value), view)),
      shouldDisableYear:
        props.shouldDisableYear &&
        ((year) => props.shouldDisableYear!(dateTimeToDate(year))),
      timezone,
      value: props.value ? dateToDateTime(props.value, { timezone }) : null,
    };

    return (
      <DateTimePicker<DateTime>
        {...convertedDateTimePickerProps}
        ref={ref}
        // Type cast slotProps and slots to work around MUI X typing issue.
        // See: https://github.com/mui/mui-x/issues/9775
        slotProps={{
          textField: {
            showCurrentTimeZone,
            timezoneProp: timezone,
            ...TextFieldProps,
          } as any,
        }}
        slots={{
          textField: TimezoneTextField as any,
        }}
        viewRenderers={{
          hours: renderTimeViewClock,
          minutes: renderTimeViewClock,
          seconds: renderTimeViewClock,
        }}
      />
    );
  },
);

export default LocatedDateTimePicker;
